import { useMemo, useState } from 'react';
import { LuBanknote, LuChevronDown, LuLogIn, LuPause, LuPlay, LuTrendingUp } from 'react-icons/lu';

import { PortfolioEngineAccount, TradingStatus } from '@ndvr/portfolio-api';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { formatCurrencyShort } from 'utils/number-formatter';

import type { AssessmentFilterKey } from './AssessmentFilter';
import { getOnboardEligibility } from './onboarding-wizard/onboard-eligibility';
import { getTradingStatusColor, getTradingStatusLabel, TRADING_STATUS_ORDER } from './trading-status-display';

interface Props {
    accounts: PortfolioEngineAccount[];
    selectedAccountIds?: Set<string>;
    tradingStatusFilter?: Set<TradingStatus>;
    onToggleTradingStatusFilter?: (status: TradingStatus) => void;
    assessmentFilter?: AssessmentFilterKey;
    onSelectAssessmentFilter?: (filter: AssessmentFilterKey) => void;
    onTradeRequest?: (accounts: PortfolioEngineAccount[]) => void;
    onUrgentWithdrawal?: (accountGUID: string) => void;
    onSetTradingStatus?: (
        accounts: PortfolioEngineAccount[],
        action: 'suspend' | 'enable',
        totalSelectedCount: number,
    ) => void;
    onOnboard?: (accounts: PortfolioEngineAccount[]) => void;
}

export const AccountOverviewSidebar = ({
    accounts,
    selectedAccountIds,
    tradingStatusFilter,
    onToggleTradingStatusFilter,
    assessmentFilter,
    onSelectAssessmentFilter,
    onTradeRequest,
    onUrgentWithdrawal,
    onSetTradingStatus,
    onOnboard,
}: Props) => {
    const [isLegendOpen, setIsLegendOpen] = useState(true);
    const hasActiveFilter = (tradingStatusFilter?.size ?? 0) > 0;
    const isStatusFiltered = (status: TradingStatus) => !hasActiveFilter || !!tradingStatusFilter?.has(status);
    const singleSelectedAccountId =
        selectedAccountIds && selectedAccountIds.size === 1 ? Array.from(selectedAccountIds)[0] : null;

    const selectedAccounts = useMemo(
        () => (selectedAccountIds ? accounts.filter((a) => selectedAccountIds.has(a.accountGUID)) : []),
        [accounts, selectedAccountIds],
    );
    const tradingStatusMutableAccounts = selectedAccounts.filter(
        (a) => a.tradingStatus !== TradingStatus.MANUAL && a.tradingStatus !== TradingStatus.EXTERNAL,
    );
    const suspendableAccounts = tradingStatusMutableAccounts.filter((a) => !a.tradingSuspendedAt);
    const allSelectedAreAutomatic = selectedAccounts.every((a) => a.tradingStatus === TradingStatus.AUTOMATIC);
    const canTradeRequest = selectedAccounts.length > 0 && allSelectedAreAutomatic;
    const tradeRequestDisabledReason = !allSelectedAreAutomatic
        ? 'Trade requests can only be sent to accounts with Automatic trading status.'
        : null;
    const singleSelectedAccount = selectedAccounts.find((a) => a.accountGUID === singleSelectedAccountId);
    const canUrgentWithdrawal = singleSelectedAccount?.tradingStatus === TradingStatus.AUTOMATIC;
    const urgentWithdrawalDisabledReason =
        singleSelectedAccount && !canUrgentWithdrawal
            ? 'Urgent withdrawals can only be sent to accounts with Automatic trading status.'
            : null;
    const { onboardableAccounts, canOnboard, onboardDisabledReason } = getOnboardEligibility(selectedAccounts);
    const enableableAccounts = tradingStatusMutableAccounts.filter((a) => !!a.tradingSuspendedAt);
    const hasSuspendable = suspendableAccounts.length > 0;
    const hasEnableable = enableableAccounts.length > 0;
    // When nothing is selected, show a single disabled "Suspend Trading" button as a placeholder.
    const showSuspendButton = hasSuspendable || !hasEnableable;
    const showEnableButton = hasEnableable;
    const tradingStatusRows = useMemo(() => {
        const total = accounts.length || 1;
        const counts = new Map<TradingStatus, number>();
        for (const account of accounts) {
            counts.set(account.tradingStatus, (counts.get(account.tradingStatus) ?? 0) + 1);
        }
        return TRADING_STATUS_ORDER.map((status) => {
            const count = counts.get(status) ?? 0;
            return {
                status,
                count,
                percent: Math.round((count / total) * 100),
                label: getTradingStatusLabel(status),
                color: getTradingStatusColor(status),
            };
        });
    }, [accounts]);

    const accountSummaries = useMemo(() => {
        const idleCashAccounts = accounts.filter((a) => a.hasIdleCash);
        const tlhAccounts = accounts.filter((a) => a.hasTaxLossHarvesting);
        return [
            {
                label: 'Idle Cash',
                value: formatCurrencyShort(sumAccountAmounts(idleCashAccounts, (a) => a.unencumberedCash)),
                count: idleCashAccounts.length,
                filter: 'idleCash' as const,
            },
            {
                label: 'TLH Opportunities',
                value: formatCurrencyShort(sumAccountAmounts(tlhAccounts, (a) => a.harvestableLoss)),
                count: tlhAccounts.length,
                filter: 'taxLossHarvesting' as const,
            },
        ];
    }, [accounts]);

    return (
        <div className="flex flex-col gap-6">
            <Section title="Account Overview">
                <Card>
                    <CardContent className="p-4">
                        <Collapsible open={isLegendOpen} onOpenChange={setIsLegendOpen}>
                            <div className="mb-3 flex items-center justify-between">
                                <div className="text-sm font-medium">Trading Status</div>
                                <CollapsibleTrigger
                                    className="text-cocoa-500 hover:text-cocoa-800 inline-flex h-5 w-5 items-center justify-center rounded transition"
                                    aria-label={isLegendOpen ? 'Collapse legend' : 'Expand legend'}
                                >
                                    <LuChevronDown
                                        className={`transition-transform ${isLegendOpen ? '' : '-rotate-90'}`}
                                    />
                                </CollapsibleTrigger>
                            </div>
                            <div className="mb-4 flex h-2 w-full overflow-hidden rounded">
                                {tradingStatusRows.map((s) =>
                                    s.percent > 0 ? (
                                        <Tooltip key={s.status}>
                                            <TooltipTrigger asChild>
                                                <button
                                                    type="button"
                                                    onClick={() => onToggleTradingStatusFilter?.(s.status)}
                                                    disabled={!onToggleTradingStatusFilter}
                                                    aria-label={`Filter by ${s.label}`}
                                                    aria-pressed={
                                                        hasActiveFilter && !!tradingStatusFilter?.has(s.status)
                                                    }
                                                    className={`${s.color} cursor-pointer transition-opacity hover:opacity-80 ${
                                                        isStatusFiltered(s.status) ? '' : 'opacity-30'
                                                    }`}
                                                    style={{ width: `${s.percent}%` }}
                                                />
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                {s.count} {s.label} · {s.percent}%
                                            </TooltipContent>
                                        </Tooltip>
                                    ) : null,
                                )}
                            </div>
                            <CollapsibleContent className="flex flex-col gap-1 text-sm">
                                {tradingStatusRows.map((s) => {
                                    const dim = !isStatusFiltered(s.status);
                                    return (
                                        <button
                                            key={s.status}
                                            type="button"
                                            onClick={() => onToggleTradingStatusFilter?.(s.status)}
                                            disabled={!onToggleTradingStatusFilter}
                                            aria-pressed={hasActiveFilter && !!tradingStatusFilter?.has(s.status)}
                                            className={`hover:bg-cocoa-100 -mx-1 flex cursor-pointer items-center justify-between rounded px-1 py-0.5 text-left transition ${
                                                dim ? 'opacity-40' : ''
                                            }`}
                                        >
                                            <div className="flex items-center gap-2">
                                                <span className={`h-2 w-2 rounded-full ${s.color}`} />
                                                <span>{s.label}</span>
                                            </div>
                                            <div className="text-muted-foreground flex items-center gap-4 tabular-nums">
                                                <span>{s.percent}%</span>
                                                <span className="text-foreground">{s.count}</span>
                                            </div>
                                        </button>
                                    );
                                })}
                            </CollapsibleContent>
                        </Collapsible>
                    </CardContent>
                </Card>
                <div className="grid grid-cols-2 gap-3">
                    {accountSummaries.map((summary) => {
                        const selected = assessmentFilter === summary.filter;
                        return (
                            <button
                                key={summary.label}
                                type="button"
                                onClick={() => onSelectAssessmentFilter?.(summary.filter)}
                                disabled={!onSelectAssessmentFilter}
                                aria-pressed={selected}
                                className={`card bg-card text-card-foreground rounded-lg border text-left transition hover:border-neutral-500 ${
                                    selected ? 'border-neutral-800 shadow-sm' : ''
                                }`}
                            >
                                <CardContent className="p-3">
                                    <div className="text-cocoa-500 text-xs">{summary.label}</div>
                                    <div className="text-cocoa-1000 mt-1 text-xl font-semibold">{summary.value}</div>
                                    <div className="text-cocoa-500 mt-1 text-xs">
                                        across {summary.count} {summary.count === 1 ? 'account' : 'accounts'}
                                    </div>
                                </CardContent>
                            </button>
                        );
                    })}
                </div>
            </Section>

            <Section title="Quick Actions">
                <OnboardButton
                    canOnboard={canOnboard}
                    disabledReason={onboardDisabledReason}
                    onboardableAccounts={onboardableAccounts}
                    onOnboard={onOnboard}
                />
                <Tooltip>
                    <TooltipTrigger asChild>
                        <span className={tradeRequestDisabledReason ? 'inline-flex' : 'contents'}>
                            <Button
                                variant="outline"
                                className="w-full justify-start"
                                disabled={!canTradeRequest || !onTradeRequest}
                                onClick={() => {
                                    if (!canTradeRequest) return;
                                    onTradeRequest?.(selectedAccounts);
                                }}
                            >
                                <LuTrendingUp />
                                Trade Request
                            </Button>
                        </span>
                    </TooltipTrigger>
                    {tradeRequestDisabledReason && <TooltipContent>{tradeRequestDisabledReason}</TooltipContent>}
                </Tooltip>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <span className={urgentWithdrawalDisabledReason ? 'inline-flex' : 'contents'}>
                            <Button
                                variant="outline"
                                className="w-full justify-start"
                                disabled={!singleSelectedAccountId || !canUrgentWithdrawal || !onUrgentWithdrawal}
                                onClick={() => {
                                    if (!singleSelectedAccountId || !canUrgentWithdrawal) return;
                                    onUrgentWithdrawal?.(singleSelectedAccountId);
                                }}
                            >
                                <LuBanknote />
                                Urgent Withdrawal
                            </Button>
                        </span>
                    </TooltipTrigger>
                    {urgentWithdrawalDisabledReason && (
                        <TooltipContent>{urgentWithdrawalDisabledReason}</TooltipContent>
                    )}
                </Tooltip>
                <div className="flex flex-col gap-2 sm:flex-row [&>*]:flex-1">
                    {showSuspendButton && (
                        <Button
                            variant="outline"
                            className="justify-start"
                            disabled={!hasSuspendable || !onSetTradingStatus}
                            onClick={() => {
                                if (!hasSuspendable) return;
                                onSetTradingStatus?.(suspendableAccounts, 'suspend', selectedAccounts.length);
                            }}
                        >
                            <LuPause />
                            Suspend Trading
                        </Button>
                    )}
                    {showEnableButton && (
                        <Button
                            variant="outline"
                            className="justify-start"
                            disabled={!hasEnableable || !onSetTradingStatus}
                            onClick={() => {
                                if (!hasEnableable) return;
                                onSetTradingStatus?.(enableableAccounts, 'enable', selectedAccounts.length);
                            }}
                        >
                            <LuPlay />
                            Enable Trading
                        </Button>
                    )}
                </div>
            </Section>
        </div>
    );
};

const OnboardButton = ({
    canOnboard,
    disabledReason,
    onboardableAccounts,
    onOnboard,
}: {
    canOnboard: boolean;
    disabledReason: string | null;
    onboardableAccounts: PortfolioEngineAccount[];
    onOnboard?: (accounts: PortfolioEngineAccount[]) => void;
}) => (
    <Tooltip>
        <TooltipTrigger asChild>
            <span className={disabledReason ? 'inline-flex' : 'contents'}>
                <Button
                    variant="outline"
                    className="w-full justify-start"
                    disabled={!canOnboard || !onOnboard}
                    onClick={() => canOnboard && onOnboard?.(onboardableAccounts)}
                >
                    <LuLogIn />
                    Onboard
                </Button>
            </span>
        </TooltipTrigger>
        {disabledReason && <TooltipContent>{disabledReason}</TooltipContent>}
    </Tooltip>
);

const sumAccountAmounts = (
    accounts: PortfolioEngineAccount[],
    getAmount: (account: PortfolioEngineAccount) => string | number | undefined,
) =>
    accounts.reduce((total, account) => {
        const amount = Number(getAmount(account) ?? 0);
        return Number.isFinite(amount) ? total + amount : total;
    }, 0);

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <div className="flex flex-col gap-3">
        <h2 className="text-lg font-semibold">{title}</h2>
        {children}
    </div>
);
